import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';

import CommonAnimatedLoader from '../../components/common/animated/loader';
import CommonAnimatedSpinner from '../../components/common/animated/spinner';
import { useAuth } from '../../hooks/use-auth';
import { Location } from '../../entities/location';
import { Product } from '../../entities/product';
import { Site } from '../../entities/site';
import * as locationsService from '../../services/api/locations';
import type { LocationPayload } from '../../services/api/locations';
import * as sitesService from '../../services/api/sites';
import * as productsService from '../../services/api/products';

interface ComponentProps {
  isEdit?: boolean;
}

interface LocationFormValues {
  title: string;
  partnerName: string;
  remoteId: string;
  locationParentId?: number | null;
  hasRemoteRegistration: boolean;
  address: string;
  workingHours: string;
  phone: string;
  registrationUrl: string;
  status: string;
  hideInWeb: boolean;
  siteIds: number[];
  productIds: number[];
  upsellProductId?: number | null;
  latitude: string;
  longitude: string;
  email: string;
}

interface ProductOption extends Product {
  siteDomain?: string;
}

interface LocationFormResources {
  sites: Site[];
  parentLocations: Location[];
  products: ProductOption[];
  initialValues: LocationFormValues;
  isLoading: boolean;
}

const DEFAULT_FORM_VALUES: LocationFormValues = {
  title: '',
  partnerName: '',
  remoteId: '',
  locationParentId: null,
  hasRemoteRegistration: false,
  address: '',
  workingHours: '',
  phone: '',
  registrationUrl: '',
  status: 'active',
  hideInWeb: false,
  siteIds: [],
  productIds: [],
  upsellProductId: null,
  latitude: '',
  longitude: '',
  email: '',
};

const toStringValue = (value?: string | number | null): string => {
  if (value === undefined || value === null) {
    return '';
  }

  return `${value}`.trim();
};

const toNullableString = (value?: string | null): string | null => {
  if (value === undefined || value === null) {
    return null;
  }

  const trimmed = `${value}`.trim();

  return trimmed.length ? trimmed : null;
};

const mapLocationToFormValues = (location?: Location | null): LocationFormValues => ({
  ...DEFAULT_FORM_VALUES,
  title: location?.title || '',
  partnerName: toStringValue(location?.partnerName),
  remoteId: toStringValue(location?.remoteId),
  locationParentId: location?.locationParentId ?? null,
  hasRemoteRegistration: Boolean(location?.hasRemoteRegistration),
  address: toStringValue(location?.address),
  workingHours: toStringValue(location?.workingHours),
  phone: toStringValue(location?.phone),
  registrationUrl: toStringValue(location?.registrationUrl),
  status: location?.status || 'active',
  hideInWeb: location ? location.hideInWeb === 1 || location.hideInWeb === true : false,
  siteIds: location?.siteIds || [],
  productIds: location?.productIds || [],
  upsellProductId: location?.upsellProductId ?? null,
  latitude: toStringValue(location?.latitude),
  longitude: toStringValue(location?.longitude),
  email: toStringValue(location?.email),
});

const buildLocationPayload = (values: LocationFormValues): LocationPayload => ({
  title: values.title,
  partnerName: toNullableString(values.partnerName),
  remoteId: toNullableString(values.remoteId),
  locationParentId: values.locationParentId ?? null,
  hasRemoteRegistration: values.hasRemoteRegistration,
  address: toNullableString(values.address),
  workingHours: toNullableString(values.workingHours),
  phone: toNullableString(values.phone),
  registrationUrl: toNullableString(values.registrationUrl),
  status: values.status,
  hideInWeb: values.hideInWeb,
  siteIds: values.siteIds || [],
  productIds: values.productIds || [],
  upsellProductId: values.upsellProductId ?? null,
  latitude: toNullableString(values.latitude),
  longitude: toNullableString(values.longitude),
  email: toNullableString(values.email),
});

const collectProductsForSites = async (sites: Site[]): Promise<ProductOption[]> => {
  if (!sites.length) {
    return [];
  }

  const responses = await Promise.all(
    sites.map(async (site) => {
      const siteProducts = await productsService.list(site.id);

      return siteProducts.map((product) => ({
        ...product,
        siteDomain: site.domain,
        siteId: product.siteId || site.id,
      }));
    }),
  );

  const uniqueProducts = new Map<number, ProductOption>();

  responses.flat().forEach((product) => {
    if (!uniqueProducts.has(product.id)) {
      uniqueProducts.set(product.id, product);
    }
  });

  return Array.from(uniqueProducts.values()).sort((a, b) => a.title.localeCompare(b.title));
};

const useLocationFormResources = (token: string, locationId?: number): LocationFormResources => {
  const [resources, setResources] = useState<LocationFormResources>({
    sites: [],
    parentLocations: [],
    products: [],
    initialValues: DEFAULT_FORM_VALUES,
    isLoading: true,
  });

  useEffect(() => {
    let isActive = true;

    const load = async () => {
      setResources((prev) => ({ ...prev, isLoading: true }));

      try {
        const [sitesResponse, locationsResponse, locationDetails] = await Promise.all([
          sitesService.list(token),
          locationsService.list(token),
          locationId ? locationsService.one(token, locationId) : Promise.resolve(null),
        ]);

        const sites = sitesResponse || [];
        const parentLocations = (locationsResponse || []).filter((location) => !location.locationParentId);
        const products = await collectProductsForSites(sites);
        const initialValues = mapLocationToFormValues(locationDetails);

        if (isActive) {
          setResources({
            sites,
            parentLocations,
            products,
            initialValues,
            isLoading: false,
          });
        }
      } catch (error) {
        if (isActive) {
          setResources((prev) => ({ ...prev, isLoading: false }));
        }
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, [token, locationId]);

  return resources;
};

function LocationsFormPageComponent({ isEdit }: ComponentProps) {
  const { token } = useAuth();
  const { id } = useParams();
  const parsedLocationId = id ? Number.parseInt(id, 10) : undefined;
  const locationId = parsedLocationId && !Number.isNaN(parsedLocationId) ? parsedLocationId : undefined;
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { sites, parentLocations, products, initialValues, isLoading } = useLocationFormResources(token, locationId);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<LocationFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const parentOptions = useMemo(() => {
    if (!parentLocations.length) {
      return [];
    }

    return parentLocations.filter((location) => location.id !== locationId);
  }, [parentLocations, locationId]);

  const productLabel = useCallback(
    (product: ProductOption) => (product.siteDomain ? `${product.title} (${product.siteDomain})` : product.title),
    [],
  );

  const onSubmit = async (values: LocationFormValues) => {
    setIsSubmitting(true);

    try {
      const payload = buildLocationPayload(values);

      if (isEdit && locationId) {
        await locationsService.update(token, locationId, payload);
      } else {
        await locationsService.create(token, payload);
      }

      navigate('/locations');
    } finally {
      setIsSubmitting(false);
    }
  };

  const showLoader = isLoading || isSubmitting;

  return (
    <div>
      {showLoader && <CommonAnimatedLoader />}
      <header className="mb-3 mt-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold leading-tight text-gray-900">
            {isEdit ? 'Edit location' : 'Create new location'}
          </h1>
        </div>
      </header>
      <main>
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit(onSubmit)} className="shadow border-b border-gray-200 sm:rounded-lg bg-white">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 p-4">
              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="title">
                  Title
                </label>
                <input
                  id="title"
                  type="text"
                  className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  {...register('title', { required: true })}
                />
                {errors.title && <p className="text-xs text-red-600 mt-1">Title is required.</p>}
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="partnerName">
                  Partner name
                </label>
                <input
                  id="partnerName"
                  type="text"
                  className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  {...register('partnerName')}
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="remoteId">
                  Remote ID
                </label>
                <input
                  id="remoteId"
                  type="text"
                  className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  {...register('remoteId')}
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  {...register('email')}
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="address">
                  Address
                </label>
                <input
                  id="address"
                  type="text"
                  className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  {...register('address')}
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="phone">
                  Phone
                </label>
                <input
                  id="phone"
                  type="text"
                  className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  {...register('phone')}
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="workingHours">
                  Working hours
                </label>
                <input
                  id="workingHours"
                  type="text"
                  className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  {...register('workingHours')}
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="registrationUrl">
                  Registration URL
                </label>
                <input
                  id="registrationUrl"
                  type="text"
                  className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  {...register('registrationUrl')}
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="locationParentId">
                  Parent location
                </label>
                <Controller
                  control={control}
                  name="locationParentId"
                  render={({ field }) => (
                    <Select
                      inputId="locationParentId"
                      isClearable
                      isLoading={isLoading}
                      options={parentOptions}
                      placeholder="Select parent location"
                      getOptionValue={(option: Location) => `${option.id}`}
                      getOptionLabel={(option: Location) => option.title}
                      value={parentOptions.find((location) => location.id === field.value) || null}
                      onChange={(option) => field.onChange(option ? option.id : null)}
                    />
                  )}
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="siteIds">
                  Sites
                </label>
                <Controller
                  control={control}
                  name="siteIds"
                  render={({ field }) => (
                    <Select
                      inputId="siteIds"
                      isMulti
                      isClearable
                      isLoading={isLoading}
                      options={sites}
                      placeholder="Select sites"
                      getOptionValue={(option: Site) => `${option.id}`}
                      getOptionLabel={(option: Site) => option.domain}
                      value={sites.filter((site) => field.value?.includes(site.id))}
                      onChange={(selected) => field.onChange(selected ? selected.map((site) => site.id) : [])}
                    />
                  )}
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="productIds">
                  Products
                </label>
                {isLoading && <CommonAnimatedSpinner sm />}
                {!isLoading && products.length === 0 && <p className="text-sm text-gray-500">No products available.</p>}
                {products.length > 0 && (
                  <Controller
                    control={control}
                    name="productIds"
                    render={({ field }) => (
                      <Select
                        inputId="productIds"
                        isMulti
                        isClearable
                        options={products}
                        placeholder="Select products"
                        getOptionValue={(option: ProductOption) => `${option.id}`}
                        getOptionLabel={(option: ProductOption) => productLabel(option)}
                        value={products.filter((product) => field.value?.includes(product.id))}
                        onChange={(selected) => field.onChange(selected ? selected.map((product) => product.id) : [])}
                      />
                    )}
                  />
                )}
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="upsellProductId">
                  Upsell product
                </label>
                <Controller
                  control={control}
                  name="upsellProductId"
                  render={({ field }) => (
                    <Select
                      inputId="upsellProductId"
                      isClearable
                      options={products}
                      placeholder="Select upsell product"
                      getOptionValue={(option: ProductOption) => `${option.id}`}
                      getOptionLabel={(option: ProductOption) => productLabel(option)}
                      value={products.find((product) => product.id === field.value) || null}
                      onChange={(selected) => field.onChange(selected ? selected.id : null)}
                    />
                  )}
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="latitude">
                  Latitude
                </label>
                <input
                  id="latitude"
                  type="text"
                  className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  {...register('latitude')}
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="longitude">
                  Longitude
                </label>
                <input
                  id="longitude"
                  type="text"
                  className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  {...register('longitude')}
                />
              </div>

              <div className="sm:col-span-3">
                <label className="block text-sm font-medium text-gray-700" htmlFor="status">
                  Status
                </label>
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <select
                      id="status"
                      className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      value={field.value}
                      onChange={(event) => field.onChange(event.target.value)}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  )}
                />
              </div>

              <div className="sm:col-span-3 flex items-center mt-6">
                <Controller
                  control={control}
                  name="hasRemoteRegistration"
                  render={({ field }) => (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        checked={field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                      />
                      <span className="ml-2 text-sm text-gray-700">Has remote registration</span>
                    </label>
                  )}
                />
              </div>

              <div className="sm:col-span-3 flex items-center mt-6">
                <Controller
                  control={control}
                  name="hideInWeb"
                  render={({ field }) => (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        checked={field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                      />
                      <span className="ml-2 text-sm text-gray-700">Hide in web</span>
                    </label>
                  )}
                />
              </div>
            </div>
            <div className="px-4 py-3 bg-gray-50 rounded-b-lg flex">
              <div className="pt-5">
                <div className="flex">
                  <button
                    type="submit"
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/locations')}
                    className="ml-3 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  {isSubmitting && (
                    <div className="mt-2 pl-3">
                      <CommonAnimatedSpinner sm />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

LocationsFormPageComponent.defaultProps = {
  isEdit: false,
};

export default LocationsFormPageComponent;
